import { useToast } from "@repo/ui";
import { PasswordConfirmationModal } from "@/components/security/password-confirmation-modal";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { deleteDispense } from "@/lib/api/dispenses";
import { ApiClientError } from "@/lib/api/client";
import type { Dispense } from "@/lib/types/inventory";

export interface DispenseDeleteDialogProps {
  dispense: Dispense | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DispenseDeleteDialog({
  dispense,
  open,
  onOpenChange,
  onSuccess,
}: DispenseDeleteDialogProps) {
  const { toast } = useToast();
  const { requestApproval, isApprovalPending } = useApproval();

  if (!dispense) return null;

  const deleteWithToken = async (password: string, approvalToken?: string) => {
    try {
      await deleteDispense(dispense.id, { password, approvalToken });

      toast({
        title: "Dispense Deleted",
        description: `Dispense RX #${dispense.rx_number} has been deleted.`,
        variant: "success",
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => deleteWithToken(password, token),
          actionDescription: `Delete dispense RX #${dispense.rx_number}`,
        });
        return;
      }
      throw error;
    }
  };

  return (
    <PasswordConfirmationModal
      open={open && !isApprovalPending}
      onClose={() => onOpenChange(false)}
      onSuccess={(password) => deleteWithToken(password)}
      title="Delete Dispense - Password Required"
      description={`You are about to delete dispense RX #${dispense.rx_number} for ${dispense.inventory?.item_name || "this item"}. This action will adjust inventory and create an audit trail. Enter your password to confirm.`}
      actionLabel="Delete Dispense"
    />
  );
}
